import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import url from '../../../Component/server';
import { InventoryUpdate } from './inventory';

export type StockOption = { seq: number; option1: string; option2: string; current_cnt: number; init_cnt: number };
export default function useOptionInventory(productSeq: number | undefined, enabled: boolean, update?: InventoryUpdate, revision?: number, poll = false) {
  const [state, setState] = useState<{ productSeq?: number; options: StockOption[]; loading: boolean; error: boolean }>({ options: [], loading: false, error: false });
  const [retry, setRetry] = useState(0);
  // Accumulate events during a request so a slow HTTP snapshot cannot undo an
  // already received stock change, even when the next event is another product.
  const events = useRef(new Map<number, InventoryUpdate>());
  const eventSequence = useRef(0);
  useEffect(() => {
    if (!update) return;
    eventSequence.current += 1; events.current.set(update.productSeq, update);
    setState(current => current.productSeq === update.productSeq ? { ...current, options: overlay(current.options, update) } : current);
  }, [update]);
  useEffect(() => {
    if (!enabled || !productSeq) return;
    let active = true, request = 0, busy = false;
    setState({ productSeq, options: [], loading: true, error: false });
    const load = async () => {
      if (busy || document.visibilityState === 'hidden') return;
      busy = true;
      const id = ++request, eventAtStart = eventSequence.current;
      try {
        const { data } = await axios.get(url.Communication_url.product_option_info, { params: { product_seq: productSeq }, timeout: 10000 });
        if (!active || id !== request) return;
        if (!Array.isArray(data?.options) || data.options.some((row: StockOption) => !Number.isSafeInteger(Number(row.current_cnt)) || Number(row.current_cnt) < 0)) throw new Error('Invalid option inventory');
        const options = eventSequence.current !== eventAtStart ? overlay(data.options, events.current.get(productSeq)) : data.options;
        setState({ productSeq, options, loading: false, error: false });
      } catch { if (active && id === request) setState(current => ({ ...current, loading: false, error: true })); }
      finally { busy = false; }
    };
    void load();
    const focus = () => { void load(); };
    window.addEventListener('focus', focus); document.addEventListener('visibilitychange', focus);
    const timer = poll ? setInterval(() => void load(), 5000) : undefined;
    return () => { active = false; request += 1; if (timer) clearInterval(timer); window.removeEventListener('focus', focus); document.removeEventListener('visibilitychange', focus); };
  }, [enabled, productSeq, revision, retry, poll]);
  return { ...(enabled && state.productSeq === productSeq ? state : { options: [], loading: enabled, error: false }), retry: () => setRetry(current => current + 1) };
}
function overlay(rows: StockOption[], update?: InventoryUpdate) {
  if (!update) return rows;
  const stocks = new Map(update.options?.map(row => [Number(row.seq), Number(row.currentCnt)]));
  return rows.map(row => stocks.has(Number(row.seq)) ? { ...row, current_cnt: stocks.get(Number(row.seq))! } : row);
}
